/**
 * Gestión de metadatos SEO en cliente (sin dependencias externas).
 *
 * Reglas importantes:
 *  - `canonical` se construye SIEMPRE con `pathname`, nunca con la query string.
 *    Si un filtro (`/ads?cat=x`) se declara canónico de sí mismo, Google lo
 *    trata como URL distinta; apuntando a `/ads` se consolida la signals.
 *  - Las imágenes para `og:image` / `twitter:image` solo admiten JPG/PNG:
 *    las previews de WhatsApp/Facebook/LinkedIn no son fiables con WebP.
 *    Si la imagen no es JPG/PNG se usa el og-image.jpg del sitio.
 *  - No se emiten etiquetas `hreflang`: esta app no tiene URLs por idioma
 *    (i18n es client-side sin rutas /en /id /es), así que cualquier hreflang
 *    sería inválido y Google lo descartaría.
 */

export const SITE_URL = 'https://terserah.id';
export const DEFAULT_TITLE = 'Terserah - Buy and Sell in Indonesia | #1 Marketplace';
export const DEFAULT_DESCRIPTION =
  "Buy and sell products on Indonesia's leading marketplace. Electronics, fashion, home, vehicles and more. Post your ad for free on Terserah.";
export const SOCIAL_FALLBACK_IMAGE = `${SITE_URL}/og-image.jpg`;

const OG_LOCALE = { id: 'id_ID', en: 'en_US', es: 'es_ES' };

/** Solo JPG/PNG: es lo que las redes sociales renderizan de forma fiable. */
export const isSocialSafeImage = (url) => /\.(jpe?g|png)(?:[?#]|$)/i.test(String(url || ''));

/** Devuelve una imagen apta para previews sociales (o el fallback del sitio). */
export const socialImage = (url) => (isSocialSafeImage(url) ? url : SOCIAL_FALLBACK_IMAGE);

const setMeta = (key, content, property = false) => {
  if (!content) return;
  const attribute = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export const setCanonical = (href) => {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

/** Elimina hreflang de despliegues anteriores (eran inválidos). */
export const removeHreflang = () => {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
};

/**
 * Aplica title, description, canonical, Open Graph, Twitter Card y robots.
 * Devuelve el título final por comodidad.
 */
export const applyPageMeta = ({
  title,
  description,
  path,
  image,
  type = 'website',
  keywords,
  lang = 'en',
  noindex = false,
} = {}) => {
  if (typeof document === 'undefined') return title || DEFAULT_TITLE;

  const fullTitle = title || DEFAULT_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  const cleanPath = (path || window.location.pathname || '/').split('?')[0];
  const url = `${SITE_URL}${cleanPath === '/' ? '' : cleanPath}`;
  const img = socialImage(image);

  document.title = fullTitle;
  document.documentElement.lang = lang;

  setMeta('description', desc);
  if (keywords) setMeta('keywords', keywords);
  setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');

  setMeta('og:title', fullTitle, true);
  setMeta('og:description', desc, true);
  setMeta('og:url', url, true);
  setMeta('og:image', img, true);
  setMeta('og:type', type, true);
  setMeta('og:site_name', 'Terserah', true);
  setMeta('og:locale', OG_LOCALE[lang] || 'en_US', true);

  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', fullTitle);
  setMeta('twitter:description', desc);
  setMeta('twitter:url', url);
  setMeta('twitter:image', img);

  setCanonical(url);

  return fullTitle;
};

export default applyPageMeta;
